import { useEffect, useState } from "react";
import { Plus, Trash2, Ban, Globe } from "lucide-react";
import ConfirmModal from "shared/ConfirmModal";
import BlockedCompanyForm from "./ui/BlockedCompanyForm";
import {
  BlockedCompany,
  BlockedCompanyCreate,
  createBlockedCompany,
  deleteBlockedCompany,
  listBlockedCompanies,
} from "./api/BlockedCompaniesApi";

export default function BlockedCompaniesTab() {
  const [companies, setCompanies] = useState<BlockedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<BlockedCompany | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      setCompanies(await listBlockedCompanies());
    } catch {
      setError("Failed to load blocked companies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  async function handleCreate(data: BlockedCompanyCreate) {
    const created = await createBlockedCompany(data);
    setCompanies((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteBlockedCompany(pendingDelete.id);
      setCompanies((prev) => prev.filter((c) => c.id !== pendingDelete.id));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  return (
    <div className="space-y-6">
      {pendingDelete && (
        <ConfirmModal
          title="Unblock company"
          message={`Stop blocking "${pendingDelete.name}"? Its job postings will be allowed through again on future syncs.`}
          confirmLabel="Unblock"
          variant="danger"
          confirming={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {showForm && (
        <BlockedCompanyForm onSave={handleCreate} onClose={() => setShowForm(false)} />
      )}

      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-inter">Blocked Companies</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            Job postings from these companies are silently dropped during LinkedIn Jobs and TheirStack Jobs ingestion — they never reach the database.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          Block Company
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
          <button onClick={() => setError("")} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : companies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl">
          <Ban className="w-10 h-10 mb-3 text-muted-foreground" />
          <p className="font-inter text-sm">No companies blocked.</p>
          <button onClick={() => setShowForm(true)} className="mt-2 text-primary hover:text-primary/80 text-sm font-inter underline">
            Block the first company
          </button>
        </div>
      ) : (
        <div className="grid gap-3 max-w-3xl">
          {companies.map((company) => (
            <div
              key={company.id}
              className="flex items-start gap-4 px-5 py-4 bg-surface/60 border border-border/60 rounded-xl hover:border-border transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground font-inter">{company.name}</p>
                {company.domain && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Globe size={11} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-inter">{company.domain}</span>
                  </div>
                )}
                {company.reason && (
                  <p className="text-xs text-muted-foreground font-inter mt-1.5">{company.reason}</p>
                )}
              </div>
              <button
                onClick={() => setPendingDelete(company)}
                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface-muted rounded-lg transition-colors shrink-0"
                title="Unblock"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
